// Desktop-style list selection helpers shared by the data browser and the layer list.

export interface Rect {
  left: number
  top: number
  right: number
  bottom: number
}

/** Ids between `from` and `to` (inclusive) in `ordered`, or [] if either is missing. */
export const rangeIds = (ordered: string[], from: string, to: string): string[] => {
  const a = ordered.indexOf(from)
  const b = ordered.indexOf(to)
  if (a < 0 || b < 0) return []
  return ordered.slice(Math.min(a, b), Math.max(a, b) + 1)
}

export const toggleId = (selected: string[], id: string): string[] =>
  selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]

/**
 * Click semantics:
 * - `toggle` (Ctrl/Cmd+click, checkbox): add/remove `id`;
 * - plain click (`toggle: false`): selection becomes just `id`, or empty when
 *   `id` already was the only selected item;
 * - Shift+click adds the anchor→id range to the selection (keeps the anchor;
 *   a missing anchor falls back to the plain/toggle rule).
 */
export const applyClick = (
  selected: string[],
  ordered: string[],
  anchor: string | null,
  id: string,
  { shift = false, toggle = true }: { shift?: boolean; toggle?: boolean } = {},
): { selected: string[]; anchor: string } => {
  if (shift && anchor) {
    const range = rangeIds(ordered, anchor, id)
    if (range.length) {
      const set = new Set(selected)
      return { selected: [...selected, ...range.filter((r) => !set.has(r))], anchor }
    }
  }
  if (toggle) return { selected: toggleId(selected, id), anchor: id }
  const onlyThis = selected.length === 1 && selected[0] === id
  return { selected: onlyThis ? [] : [id], anchor: id }
}

export const rectFrom = (x1: number, y1: number, x2: number, y2: number): Rect => ({
  left: Math.min(x1, x2),
  top: Math.min(y1, y2),
  right: Math.max(x1, x2),
  bottom: Math.max(y1, y2),
})

export const intersects = (a: Rect, b: Rect): boolean =>
  a.left <= b.right && a.right >= b.left && a.top <= b.bottom && a.bottom >= b.top

/** Rect clipped to `clip`, or null when they don't overlap. */
export const clipRect = (r: Rect, clip: Rect): Rect | null => {
  const c = {
    left: Math.max(r.left, clip.left),
    top: Math.max(r.top, clip.top),
    right: Math.min(r.right, clip.right),
    bottom: Math.min(r.bottom, clip.bottom),
  }
  return c.left <= c.right && c.top <= c.bottom ? c : null
}

/** Ids of items whose rect intersects `rect`, in item order. */
export const rectsHit = (items: { id: string; rect: Rect }[], rect: Rect): string[] =>
  items.filter((i) => intersects(i.rect, rect)).map((i) => i.id)
